import React from 'react';
import { useIntl } from 'react-intl';
import { Location, useLocation, useParams } from 'react-router-dom';

import { useCurrentTheme } from 'app/shared/theme';
import {
  Box,
  Icon,
  LoadingBlock,
  NavigationLink,
  Typography,
} from 'app/shared/components/atoms';
import { BasePageBox, SEO } from 'app/shared/components/molecules';
import { GlobalError } from 'app/shared/components/pages/Status';
import Layout from 'app/shared/layouts/MainCommonTheme';
import { GetArtistPublicProfile } from 'app/fan/graphql/artists/queryHooks';
import { ArtistSocialIcons } from 'app/fan/components/molecules';
import {
  FilmstripSection,
  FollowArtistButton,
} from 'app/fan/components/organisms';
import { ReactComponent as BackArrow } from 'icons/move-left.svg';

import ArtistHero from './ArtistHero';
import ArtistPriorCities from './ArtistPriorCities';
import ArtistVideos from './ArtistVideos';

const getUrlParamValue = (location: Location, param: string) => {
  return new URLSearchParams(location.search).get(param);
};

const ArtistProfile = () => {
  const { slug } = useParams();
  const location = useLocation();
  const intl = useIntl();
  const { colors } = useCurrentTheme();

  // When a user clicks the follow button in an email it will
  // open this page with query param ?followArtist=true.
  // In that case, we attempt to follow the artist.
  const autoFollow = getUrlParamValue(location, 'followArtist') === 'true';

  const {
    data: artistData,
    loading: artistLoading,
    error: artistError,
  } = GetArtistPublicProfile({
    cachedSlug: slug,
  });

  if (artistLoading) {
    return (
      <Layout
        data-qaid="artist-page-loading"
        backgroundColor={colors.primary.white.base}
      >
        <BasePageBox
          backgroundColor={colors.primary.black.base}
          py={8}
          flexDirection="column"
          gap={6}
        >
          <LoadingBlock h="450px" />
          <LoadingBlock h="250px" />
        </BasePageBox>
        <ArtistVideos videosLoading={true} />
      </Layout>
    );
  }

  if (artistError || !artistData || !artistData.artist) {
    return <GlobalError />;
  }

  const {
    artist: {
      id,
      title,
      bannerImageUrl,
      description,
      socialUrls,
      priorCities,
      videos,
    },
  } = artistData;

  return (
    <Layout
      data-qaid="artist-page"
      backgroundColor={colors.primary.white.lighter}
    >
      <SEO title={title} canonicalUrlPath={`/artists/${slug}`} />
      <BasePageBox
        data-qaid="artist-page-information-section"
        backgroundColor={colors.primary.black.base}
        py={10}
        flexDirection="column"
        gap={6}
      >
        <NavigationLink
          href="/videos"
          variant="primary"
          flexDirection="row"
          gap={2}
          svgFillColor="none"
        >
          <Icon icon={BackArrow} size="md" />
          {intl.formatMessage({
            id: 'artist.returnToAllVideos',
          })}
        </NavigationLink>
        <ArtistHero
          title={title}
          backgroundImageUrl={bannerImageUrl}
          data-qaid="artist-hero"
        />
        <FollowArtistButton autoFollow={autoFollow} artistId={id} />
        {description && (
          <Typography
            color={colors.primary.white.base}
            data-qaid="artist-bio-text"
          >
            {description}
          </Typography>
        )}

        {socialUrls && (
          <ArtistSocialIcons
            socialUrls={socialUrls}
            data-qaid="artist-social-icons"
            pt={6}
            bt={colors.primary.white.base}
          >
            <Typography
              fontSize="fontSize75"
              fontWeight="medium"
              color={colors.primary.white.base}
            >
              {intl.formatMessage({ id: 'artist.connect' })}
            </Typography>
          </ArtistSocialIcons>
        )}
        {priorCities && (
          <Box bt={colors.primary.white.base} pt={6}>
            <ArtistPriorCities
              name={title}
              cities={priorCities}
              data-qaid="artist-prior-cities"
            />
          </Box>
        )}
      </BasePageBox>
      <FilmstripSection
        pt={10}
        pb={6}
        qaId="artist-profile-upcoming-events-filmstrip"
        artistSlug={slug}
        includeCityNameInTitle={false}
        location="artist-profile"
        invertColors={true}
      />
      <ArtistVideos
        videos={videos}
        videosLoading={artistLoading}
        artistTitle={title}
        data-qaid="artist-videos"
      />
    </Layout>
  );
};

export default ArtistProfile;
